import { setDefaultOptions, loadModules } from 'esri-loader';

export default class EvacuationZoneQuery {
    constructor(handleSetSuggestions, handleSetResults, setErrorMessage, setSearching, language) {
        this.setSuggestions = handleSetSuggestions;
        this.setResults = handleSetResults;
        this.setErrorMessage = setErrorMessage;
        this.setSearching = setSearching;
        this.featureLayers = [];
        this.language = language;
    }

    initializeSearch = async () => {
        setDefaultOptions({ version: 4.16 });
        const [Extent, FeatureLayer, Locator, Search] = await loadModules(['esri/geometry/Extent', 'esri/layers/FeatureLayer', 'esri/tasks/Locator', 'esri/widgets/Search']);

        if (document.getElementById('evac_zone_layers')) {
            const featureServers = JSON.parse(document.getElementById('evac_zone_layers').value);

 
            if (featureServers && featureServers.length > 0) {
                const countyExtent = new Extent({
                    xmin: -13751618.472402455,
                    ymin: 4595324.474435851,
                    xmax: -13619962.599153256,
                    ymax: 4700604.333155776,
                    spatialReference: {
                        wkid: 102100
                    }
                })
        
                this.search = new Search({
                    sources: [{                        
                        locator: new Locator({ url: "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer" }),
                        singleLineFieldName: "SingleLine",
                        outFields: ["*"],
                        maxResults: 1,
                        maxSuggestions: 6,
                        suggestionsEnabled: true,
                        minSuggestCharacters: 4,
                        filter: {
                            geometry: countyExtent
                        }
                    }],
                    includeDefaultSources: false
                });

                for (let layer of featureServers) {
                    const descriptionAttributes = [];
                    const descriptionAttributesEs = [];

                    for (let i = 1; i < 5; i++) {
                        if (layer[`description_${i}`]) {
                            descriptionAttributes.push(layer[`description_${i}`]);                            
                        }

                        if (layer[`description_${i}_es`]) {
                            descriptionAttributesEs.push(layer[`description_${i}_es`]);
                        }
                    }

                    this.featureLayers.push({
                        featureLayer: new FeatureLayer({
                            url: layer.url,
                        }),
                        zoneAttribute: layer.zone_attribute,
                        statusAttribute: layer.status_attribute,
                        statusAttributeEs: layer.status_attribute_es,
                        descriptionAttributes,
                        descriptionAttributesEs,
                        summaryAttribute: layer.summary_attribute,
                        summaryAttributeEs: layer.summary_attribute_es,
                        pdfMapAttribute: layer.pdf_map_url,
                        pdfMapAttributeEs: layer.pdf_map_url_es
                    });
                }
            } else {
                this.setErrorMessage(this.language === 'en' ? 'There was an error retrieving evacuation zone information. Please try again later' : 'Se produjo un error al recuperar la información de la zona de evacuación. Por favor, inténtelo de nuevo más tarde');
            }
        }
    }

    suggestResults = async input => {
        const response = await this.search.suggest(input);

        if (response?.results?.[0]?.results) {
            this.setSuggestions(response.results[0].results);
        }
    }

    searchAddress = async searchCriteria => {       
        this.setSearching(true);
        const response = await this.search.search(searchCriteria);        

        if (response?.results?.[0]?.results?.[0]?.feature) {
            await this.queryEvacZones(response.results[0].results[0].feature);
        } else {
            this.setErrorMessage(this.language === 'en' ? 'No results found for the entered address' : 'No se encontraron resultados para la dirección ingresada');
        }
        
        this.setSearching(false);        
    }

    queryEvacZones = async feature => {
        for (let layer of this.featureLayers) {            
            let evacZoneDetails = await this.queryEvacLayer(feature, layer);

            if (evacZoneDetails && feature?.attributes?.Match_addr) {
                evacZoneDetails.address = feature.attributes.Match_addr;
                this.setResults(evacZoneDetails);
                return;
            }
        }
        
        this.setErrorMessage(this.language === 'en' ? 'No results found for the entered address' : 'No se encontraron resultados para la dirección ingresada');
    }

    queryEvacLayer = async (feature, layer) => {
        const query = layer.featureLayer.createQuery();
        query.geometry = feature.geometry;
        query.outFields = ["*"];
        query.returnGeometry = false;

        const result = await layer.featureLayer.queryFeatures(query);

        if (result?.features?.[0]?.attributes) {
            let zoneInfo = {};       

            const attributes = result.features[0].attributes;

            if (this.language === 'en') {
                if (layer.zoneAttribute && attributes[layer.zoneAttribute]?.trim()) {
                    zoneInfo.evacZone = attributes[layer.zoneAttribute].trim();
                } else {
                    zoneInfo.evacZone = 'Zone ID not currently available';
                }

                if (layer.statusAttribute && attributes[layer.statusAttribute]?.trim()) {
                    zoneInfo.status = attributes[layer.statusAttribute].trim();

                    switch (attributes[layer.statusAttribute].trim()) {
                        case 'Normal':
                        case 'OPEN': 
                            zoneInfo.status = 'Normal'
                            break;
                        case 'Advisory':
                            zoneInfo.status = 'Advisory'
                            break;
                        case 'Clear to Repopulate':
                            zoneInfo.status = 'Clear to Repopulate'
                            break;
                        case 'Evacuation Order':
                        case 'ORDER':
                            zoneInfo.status = 'Evacuation Order'
                            break;
                        case 'Evacuation Warning':
                        case 'WARNING':
                            zoneInfo.status = 'Evacuation Warning'
                            break;
                        case 'Shelter in Place':
                            zoneInfo.status = 'Shelter in Place'
                            break;
                        default: 
                            zoneInfo.status = result.features[0].attributes[layer.statusAttribute].trim();
                            break;
                    }

                } else {
                    zoneInfo.status = 'Status not currently available';
                }

                if (layer.summaryAttribute && attributes[layer.summaryAttribute]?.trim()) {
                    zoneInfo.summary = attributes?.[layer.summaryAttribute].trim();
                } else {
                    zoneInfo.summary = 'Summary not currently available';
                }

                if (layer.descriptionAttributes?.length > 0 && layer.descriptionAttributes.some(descAttr => attributes[descAttr]?.trim())) {
                    zoneInfo.description = [];
                    
                    const tempDescriptions = [];
                    let descCount = 0;

                    for (let descAttr of layer.descriptionAttributes) {
                        if (attributes[descAttr]?.trim()) {
                            tempDescriptions.push(attributes[descAttr].trim())
                            descCount++;
                        }
                    }

                    if (descCount === 1) {
                        zoneInfo.description = this.processDescription(tempDescriptions[0]);
                    } else {
                        zoneInfo.description = tempDescriptions;
                    }
                } else {
                    zoneInfo.description = ['Description Not currently available'];
                }

                if (layer.pdfMapAttribute && attributes[layer.pdfMapAttribute]?.trim()) {
                    zoneInfo.pdfMapUrl = attributes[layer.pdfMapAttribute]?.trim();
                }
            } else {
                if (layer.zoneAttribute && attributes[layer.zoneAttribute]?.trim()) {
                    zoneInfo.evacZone = attributes[layer.zoneAttribute].trim();
                } else {
                    zoneInfo.evacZone = 'ID de zona no disponible actualmente';
                }

                if (layer.statusAttributeEs && attributes[layer.statusAttributeEs].trim()) {
                    zoneInfo.status = attributes[layer.statusAttributeEs].trim();
                }
                else if (layer.statusAttribute && attributes[layer.statusAttribute]?.trim()) {
                    switch (attributes[layer.statusAttribute].trim()) {
                        case 'Normal':
                        case 'OPEN': 
                            zoneInfo.status = 'Normal'
                            break;
                        case 'Advisory':
                            zoneInfo.status = 'Consultiva'
                            break;
                        case 'Clear to Repopulate':
                            zoneInfo.status = 'Claro para repoblar'
                            break;
                        case 'Evacuation Order':
                        case 'ORDER':
                            zoneInfo.status = 'Orden de evacuación'
                            break;
                        case 'Evacuation Warning':
                        case 'WARNING':
                            zoneInfo.status = 'Advertencia de evacuación'
                            break;
                        case 'Shelter in Place':
                            zoneInfo.status = 'Refugiarse en el lugar'
                            break;
                        default: 
                            zoneInfo.status = result.features[0].attributes[layer.statusAttribute].trim();
                            break;
                    }
                } else {
                    zoneInfo.status = 'Estado no disponible actualmente';
                }

                if (layer.summaryAttribute && attributes[layer.summaryAttribute]?.trim()) {
                    zoneInfo.summary = attributes?.[layer.summaryAttribute].trim();
                } else {
                    zoneInfo.summary = 'Resumen no disponible actualmente';
                }

                if (layer.descriptionAttributes?.length > 0 && layer.descriptionAttributes.some(descAttr => attributes[descAttr]?.trim())) {
                    zoneInfo.description = [];
                    
                    const tempDescriptions = [];
                    let descCount = 0;

                    for (let descAttr of layer.descriptionAttributesEs) {
                        if (attributes[descAttr]?.trim()) {
                            tempDescriptions.push(attributes[descAttr].trim())
                            descCount++;
                        }
                    }

                    if (descCount === 1) {
                        zoneInfo.description = this.processDescription(tempDescriptions[0]);
                    } else {
                        zoneInfo.description = tempDescriptions;
                    }                   
                } else {
                    zoneInfo.description = ['Descripción no disponible actualmente'];
                }

                if (layer.pdfMapAttributeEs && attributes[layer.pdfMapAttributeEs]?.trim()) {
                    zoneInfo.pdfMapUrl = attributes[layer.pdfMapAttributeEs]?.trim();
                } else if (layer.pdfMapAttribute && attributes[layer.pdfMapAttribute]?.trim()) {
                    zoneInfo.pdfMapUrl = attributes[layer.pdfMapAttribute]?.trim();
                }
            }
            return zoneInfo;            
        }

        return null;
    }

    
    processDescription = html => {
        let parser = new DOMParser();
        let description = parser.parseFromString(html.trim(), 'text/html');
        if (description.querySelectorAll('li').length > 0) {
            return Array.from(description.querySelectorAll('li')).map(line => line.innerText.trim());
        } else {
            return [html.trim()];
        }
    }
};